import React, { useMemo, useState } from 'react';
import { TextField, Button, Snackbar } from '@material-ui/core';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';
import { format, isValid, startOfDay } from 'date-fns';
import deLocale from 'date-fns/locale/de';
import MuiAlert from '@material-ui/lab/Alert';
import axios from 'axios';
import API_CONFIG from '../../../config/api';

const EMPTY = {
  firstname: '',
  secondname: '',
  emailAddress: '',
  phoneNumber: '',
  carMake: '',
  carModel: '',
  dateTime: startOfDay(new Date()),
  note: '',
  company: '',
};

function isEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());
}

function isFutureDate(value) {
  return isValid(value) && startOfDay(value).getTime() >= startOfDay(new Date()).getTime();
}

export default function OnlineTerminSection(props) {
  const [values, setValues] = useState({ ...EMPTY });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [snack, setSnack] = useState('');
  const minDate = useMemo(() => startOfDay(new Date()), []);

  const change = (key) => (event) => {
    const value = event.target.value;
    setValues((current) => ({ ...current, [key]: value }));
    if (errors[key]) {
      setErrors((current) => ({ ...current, [key]: false }));
    }
  };

  const changeDate = (value) => {
    setValues((current) => ({ ...current, dateTime: value }));
    if (errors.dateTime) {
      setErrors((current) => ({ ...current, dateTime: false }));
    }
  };

  const validate = () => {
    const next = {
      firstname: !String(values.firstname).trim(),
      secondname: !String(values.secondname).trim(),
      emailAddress: !isEmail(values.emailAddress),
      phoneNumber: !String(values.phoneNumber).trim(),
      carMake: !String(values.carMake).trim(),
      carModel: !String(values.carModel).trim(),
      dateTime: !isFutureDate(values.dateTime),
      note: !String(values.note).trim(),
    };
    setErrors(next);
    return !Object.values(next).some(Boolean);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (sending || sent) return;
    if (values.company) {
      setSent(true);
      return;
    }
    if (!validate()) {
      setSnack('Bitte füllen Sie die markierten Felder aus.');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API_CONFIG.baseURL}v3/form/appointment/`, {
        firstname: values.firstname.trim(),
        secondname: values.secondname.trim(),
        emailAddress: values.emailAddress.trim(),
        phoneNumber: values.phoneNumber.trim(),
        carMake: values.carMake.trim(),
        carModel: values.carModel.trim(),
        dateTime: format(values.dateTime, 'yyyy-MM-dd'),
        note: values.note.trim(),
      }, { timeout: 15000 });

      if (!response || !response.data || response.data.info === 'error') {
        throw new Error(response?.data?.message || 'save failed');
      }
      setSent(true);
    } catch (error) {
      const message = error.response?.data?.message || 'Die Anfrage konnte nicht gesendet werden. Bitte später erneut versuchen oder anrufen.';
      setSnack(message);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <section className="spg-section">
        <div className="spg-kicker">Online Termin</div>
        <h2 className="spg-title">Anfrage gesendet</h2>
        <div
          className="spg-lead"
          dangerouslySetInnerHTML={{
            __html: props.appointmentThanksText || '<p>Vielen Dank. Wir bestätigen den Termin so bald wie möglich.</p>',
          }}
        />
      </section>
    );
  }

  return (
    <section className="spg-section">
      <Snackbar open={Boolean(snack)} autoHideDuration={6000} onClose={() => setSnack('')}>
        <MuiAlert elevation={6} variant="filled" onClose={() => setSnack('')} severity="warning">
          {snack}
        </MuiAlert>
      </Snackbar>

      <div className="spg-kicker">Online Termin</div>
      <h2 className="spg-title">Termin anfragen</h2>
      <div
        className="spg-lead"
        dangerouslySetInnerHTML={{
          __html: props.appointmentSuggestionText || '<p>Kurzes Formular. Wir prüfen die Anfrage und melden uns zur Bestätigung.</p>',
        }}
      />

      <MuiPickersUtilsProvider utils={DateFnsUtils} locale={deLocale}>
      <form className="spg-form" onSubmit={submit} noValidate>
        <label className="spg-form-honeypot" aria-hidden="true">
          Firma
          <input tabIndex={-1} autoComplete="off" value={values.company} onChange={change('company')} />
        </label>

        <div className="spg-form-grid">
          <TextField label="Vorname" variant="outlined" fullWidth required value={values.firstname} onChange={change('firstname')} error={errors.firstname} />
          <TextField label="Nachname" variant="outlined" fullWidth required value={values.secondname} onChange={change('secondname')} error={errors.secondname} />
          <TextField label="E-Mail" type="email" variant="outlined" fullWidth required value={values.emailAddress} onChange={change('emailAddress')} error={errors.emailAddress} />
          <TextField label="Telefon" variant="outlined" fullWidth required value={values.phoneNumber} onChange={change('phoneNumber')} error={errors.phoneNumber} />
          <TextField label="Marke" variant="outlined" fullWidth required value={values.carMake} onChange={change('carMake')} error={errors.carMake} placeholder="z. B. VW" />
          <TextField label="Modell" variant="outlined" fullWidth required value={values.carModel} onChange={change('carModel')} error={errors.carModel} placeholder="z. B. Golf" />
          <KeyboardDatePicker
            className="spg-date-picker"
            label="Wunschdatum"
            format="dd.MM.yyyy"
            placeholder="TT.MM.JJJJ"
            value={values.dateTime}
            onChange={changeDate}
            minDate={minDate}
            disablePast
            autoOk
            variant="inline"
            inputVariant="outlined"
            fullWidth
            required
            error={errors.dateTime}
            helperText={errors.dateTime ? 'Bitte ein Datum im Format TT.MM.JJJJ wählen.' : undefined}
            invalidDateMessage="Ungültiges Datum (TT.MM.JJJJ)"
            minDateMessage="Bitte ein Datum ab heute wählen"
            cancelLabel="Abbrechen"
            okLabel="OK"
            KeyboardButtonProps={{ 'aria-label': 'Datum wählen' }}
          />
        </div>

        <TextField
          label="Was sollen wir machen?"
          variant="outlined"
          fullWidth
          required
          multiline
          rows={4}
          value={values.note}
          onChange={change('note')}
          error={errors.note}
          placeholder="z. B. Service, Räderwechsel, Klimaanlage, MFK-Vorbereitung"
          style={{ marginTop: 16 }}
        />

        <Button
          type="submit"
          variant="contained"
          className="spg-form-submit"
          disabled={sending}
        >
          {sending ? 'Wird gesendet …' : 'Anfrage senden'}
        </Button>
      </form>
      </MuiPickersUtilsProvider>
    </section>
  );
}
